import React from 'react';
import {
  Home,
  Gavel,
  ShowChart,
  LocalLibrary,
  LibraryBooks,
  Loop,
  GroupWork,
  Reorder,
  People,
  Publish,
  MenuBook,
  SupervisedUserCircle,
  AccountTree,
  Book,
  Dataset,
  Extension,
  Groups,
  Handyman,
  PersonPin,
  Spellcheck,
} from '@mui/icons-material';

import { Shell } from './Shell';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { Notice } from './notices/NoticeIcon';
import { Overview as GuideOverview } from './guides/Overview';
import { Details as GuideDetails } from './guides/Details';
import { EditGuideParagraphs } from './guides/EditGuideParagraphs';
import { EditGuideStandards } from './standards/EditGuide';
import { EditGuideCategories } from './categories/EditGuideCategories';
import { GuideLinksEdit } from './guides/GuideLinks';
import { ReleaseOverview } from './guides/ReleaseOverview';

import { ParagraphOverview } from './paragraphs/Overview';
import { Details as ParagraphDetails } from './paragraphs/Details';

import { StandardOverview } from './standards/Overview';
import { StandardDetails } from './standards/Details';

import { Overview as CategoryOverview } from './categories/Overview';
import { Details as CategoryDetails } from './categories/Details';

import { Overview as NoticeOverview } from './notices/Overview';

import { OverviewToggle as RevisionOverview } from './revisions/OverviewToggle';
import { Details as RevisionDetails } from './revisions/Details';

import { Overview as ResourceOverview } from './resources/Overview';

import { Overview as Dashboard } from './home/Overview';
import { Overview as DashboardOverview } from './dashboard/Overview';

import { Overview as Backlog } from './backlog/Overview';
import { Overview as SubjectResources } from './subjectResources/Overview';
import { Details as SubjectResourceDetails } from './subjectResources/Details';
import { Admin as OrganizationAdmin } from './organization/OrganizationAdmin';
import { Overview as Partners } from './partners/Overview';
import { Details as PartnerDetails } from './partners/Details';

import { Overview as Classification } from './classifications/Overview';

import { Overview as BookReferenceOverview } from './bookAdmin/Overview';

import { BookDetail } from './bookAdmin/BookDetail';
import { Overview as AppOverview } from './appAdmin/Overview';
import { AppDetail } from './appAdmin/AppDetail';
import LoadingSpinner from './LoadingSpinner';
import { ErrorBoundary } from 'react-error-boundary';
import { ArchivedGuideCmsEditPage, GuideCmsEditPage, GuideCmsReadOnlyPage } from './guides/cms/GuideCmsPage';
import { HearingsPage } from './hearings/HearingsPage';
import { Overview as DictionaryOverview } from './dictionary/Overview';
import { Details as DictionaryDetails } from './dictionary/Details';
import { Overview as DataSourcesOverview } from './dataSources/Overview';
import { Details as DataSourceDetails } from './dataSources/Details';
import { useAuth } from 'oidc-react';
import { FeatureFlags, useFeatureFlags } from './unleash';
import { Overview as CommitteeMeetings } from './committeMeetings/Overview';
import { Details as CommitteeMeetingDetails } from './committeMeetings/Details';
import { Overview as GuideArchiveOverview } from './guideArchive/Overview';
import { Overview as CommentsOverview } from './comments/Overview';
import { Notifications } from './home/Notifications';
import { ChecklistTemplateEditor } from './revisions/ChecklistTemplateEditor';

export interface RouteDef {
  path: string;
  component: React.ComponentType<any>;
  title?: React.ReactNode;
  icon?: React.ReactNode;
}

const routes: RouteDef[] = [
  { path: '/', component: Dashboard, title: 'Hjem', icon: <Home /> },
  { path: '/notifications', component: Notifications },
  { path: '/statistics', component: DashboardOverview, title: 'Statistikk', icon: <ShowChart /> },
  {
    path: '/comments',
    component: CommentsOverview,
    title: 'Tilbakemeldinger',
    icon: <PersonPin />,
  },
  { path: '/notice', component: NoticeOverview, title: `Merknader`, icon: <Notice /> },
  { path: '/notice/:id', component: NoticeOverview },
  { path: '/paragraph', component: ParagraphOverview, title: 'TEK', icon: <Gavel /> },
  { path: '/paragraph/:id', component: ParagraphDetails },
  { path: '/standard', component: StandardOverview, title: 'Standarder', icon: <LocalLibrary /> },
  { path: '/standard/:id', component: StandardDetails },
  { path: '/category', component: CategoryOverview, title: 'Fagområder', icon: <GroupWork /> },
  { path: '/category/:id', component: CategoryDetails },
  { path: '/dictionary', component: DictionaryOverview, title: 'Ordliste', icon: <Spellcheck /> },
  { path: '/dictionary/:id', component: DictionaryDetails },
  { path: '/guide', component: GuideOverview, title: 'Anvisninger', icon: <LibraryBooks /> },
  { path: '/guide/:id', component: GuideDetails },
  { path: '/guide-editor/:id', component: GuideCmsReadOnlyPage },
  { path: '/guide-editor/:id/edit', component: GuideCmsEditPage },
  { path: '/guide/paragraphs/:id', component: EditGuideParagraphs },
  { path: '/guide/standards/:id', component: EditGuideStandards },
  { path: '/guide/categories/:id', component: EditGuideCategories },
  { path: '/guide/hearings/:id', component: HearingsPage },
  { path: '/guide/links/:id', component: GuideLinksEdit },
  { path: '/backlog', component: Backlog, title: 'Planlegging', icon: <Reorder /> },
  { path: '/revision/:id', component: RevisionDetails },
  { path: '/checklist-template', component: ChecklistTemplateEditor },
  { path: '/revision', component: RevisionOverview, title: 'Revisjoner', icon: <Loop /> },
  { path: '/resource', component: ResourceOverview, title: 'Ressurser', icon: <People /> },
  { path: '/releases', component: ReleaseOverview, title: 'Publiseringer', icon: <Publish /> },
  { path: '/subjectResource', component: SubjectResources, title: 'Fagressurser', icon: <MenuBook /> },
  { path: '/subjectResource/:id', component: SubjectResourceDetails },
  { path: '/organization', component: OrganizationAdmin },
  { path: '/partner', component: Partners, title: 'Partnere', icon: <SupervisedUserCircle /> },
  { path: '/partner/:id', component: PartnerDetails },
  { path: '/classification', component: Classification, title: 'Klassifikasjonssystem', icon: <AccountTree /> },
  { path: '/bookadmin', component: BookReferenceOverview, title: 'Bøker', icon: <Book /> },
  { path: '/book/:bookTitle', component: BookDetail },
  { path: '/apps', component: AppOverview, title: 'Applikasjoner', icon: <Extension /> },
  { path: '/app/:appId', component: AppDetail },
  { path: '/datasource', component: DataSourcesOverview, title: 'Datakilder', icon: <Dataset /> },
  { path: '/datasource/:correlationId', component: DataSourceDetails },
  { path: '/meeting', component: CommitteeMeetings, title: 'Utvalgsmøter', icon: <Groups /> },
  { path: '/meeting/:id', component: CommitteeMeetingDetails },
  { path: '/archive', component: GuideArchiveOverview, title: 'Migrering', icon: <Handyman /> },
  { path: '/archive-guide-editor/:id', component: ArchivedGuideCmsEditPage },
];

const getRoutes = (featureFlags: FeatureFlags) => {
  const routesToUse = routes.filter((route) => {
    if (route.path.indexOf('/datasource') === 0) {
      return featureFlags.dataSources;
    }
    if (route.path.indexOf('/meeting') === 0) {
      return featureFlags.committeeMeetings;
    }
    if (route.path.indexOf('/dictionary') === 0) {
      return featureFlags.dictionary;
    }
    return true;
  });
  return routesToUse;
};

export function App() {
  const featureFlags = useFeatureFlags();
  const auth = useAuth();
  if (auth.isLoading || !featureFlags.flagsReady) return <LoadingSpinner />;

  const routes = getRoutes(featureFlags);
  const router = createBrowserRouter(
    [
      {
        element: <Shell routes={routes} />,
        children: routes.map((x) => {
          return { path: x.path, element: <x.component />, errorElement: <ErrorBoundary fallback={<div>Something went wrong</div>} /> };
        }),
        hydrateFallbackElement: <LoadingSpinner />,
      },
    ],
    {
      future: {
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_fetcherPersist: true,
        v7_skipActionErrorRevalidation: true,
        v7_relativeSplatPath: true,
      },
    },
  );
  return (
    <>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </>
  );
}
